import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash, CircleNotch, ArrowsClockwise, UserPlus, CheckCircle, ArrowSquareOut, CaretUp, CaretDown, Robot, Prohibit, ShieldCheck } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const ALL_PERMISSIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'admin_chat', label: 'Admin Chat' },
  { key: 'credits', label: 'Credits' },
  { key: 'edit_bots', label: 'Edit Bot' },
  { key: 'bot_settings', label: 'Bot Settings' },
  { key: 'bot_automation', label: 'Bot Automation' },
  { key: 'broadcast', label: 'Broadcast' },
  { key: 'channel_broadcast', label: 'Channel Broadcast' },
  { key: 'scheduled_broadcast', label: 'Scheduled Broadcast' },
  { key: 'broadcast_reports', label: 'Broadcast Reports' },
  { key: 'user_reports', label: 'User Reports' },
  { key: 'mobile_reports', label: 'Mobile Reports' },
  { key: 'live_chat', label: 'Live Chat' },
  { key: 'auto_messages', label: 'Auto Messages' },
  { key: 'whatsapp_chat', label: 'WhatsApp Chat' },
  { key: 'create_users', label: 'Create Sub-Admins' },
  { key: 'delete_chat', label: 'Delete Chats' },
];

export default function BotManagementPage() {
  const { setBots: setGlobalBots, setSelectedBot: setGlobalSelectedBot } = useOutletContext();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bots, setBots] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [token, setToken] = useState('');
  const [adding, setAdding] = useState(false);
  const [pinging, setPinging] = useState({});
  const [confirmBlockBot, setConfirmBlockBot] = useState(null);
  const [blockingBot, setBlockingBot] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignBot, setAssignBot] = useState(null);
  const [assignAdmin, setAssignAdmin] = useState('');
  const [assignPerms, setAssignPerms] = useState([]);

  const handleToggleBlockBot = async (bot) => {
    if (bot.is_blocked) {
      setBlockingBot(true);
      try {
        await API.post(`/bots/${bot.id}/unblock`);
        toast.success(`Bot @${bot.bot_username || bot.bot_name} unblocked`);
        fetchBots();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to unblock bot');
      } finally {
        setBlockingBot(false);
      }
    } else {
      setConfirmBlockBot(bot);
    }
  };

  const handleConfirmBlockBot = async () => {
    if (!confirmBlockBot) return;
    setBlockingBot(true);
    try {
      await API.post(`/bots/${confirmBlockBot.id}/block`);
      toast.success(`Bot @${confirmBlockBot.bot_username || confirmBlockBot.bot_name} blocked by Super Admin`);
      setConfirmBlockBot(null);
      fetchBots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to block bot');
    } finally {
      setBlockingBot(false);
    }
  };

  const fetchBots = () => {
    API.get('/bots').then(r => { setBots(r.data.bots || []); setGlobalBots(r.data.bots || []); }).catch(() => {}).finally(() => setLoading(false));
  };
  const fetchAdmins = () => API.get('/admins').then(r => setAdmins(r.data.admins || [])).catch(() => {});
  useEffect(() => { fetchBots(); fetchAdmins(); }, []);

  const addBot = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await API.post('/bots', { token });
      toast.success('Bot added successfully');
      setAddOpen(false);
      setToken('');
      fetchBots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add bot');
    } finally { setAdding(false); }
  };

  const deleteBot = async (id) => {
    if (!window.confirm('Delete this bot?')) return;
    try {
      await API.delete(`/bots/${id}`);
      toast.success('Bot deleted');
      fetchBots();
    } catch { toast.error('Failed'); }
  };

  const pingBot = async (id) => {
    setPinging(p => ({ ...p, [id]: true }));
    try {
      const { data } = await API.post(`/bots/${id}/ping`);
      toast[data.status === 'online' ? 'success' : 'error'](`Bot is ${data.status}`);
    } catch { toast.error('Ping failed'); }
    finally { setPinging(p => ({ ...p, [id]: false })); }
  };

  const openAssign = (bot) => {
    setAssignBot(bot);
    setAssignAdmin('');
    setAssignPerms([]);
    setAssignOpen(true);
  };

  // When selecting admin in assign dialog, pre-fill their existing permissions
  const handleAdminSelect = (adminId) => {
    setAssignAdmin(adminId);
    const admin = admins.find(a => a.id === adminId);
    if (admin && admin.permissions) {
      setAssignPerms([...admin.permissions]);
    } else {
      setAssignPerms([]);
    }
  };

  const submitAssign = async () => {
    if (!assignAdmin) return toast.error('Select an admin');
    try {
      await API.post(`/bots/${assignBot.id}/assign`, { admin_id: assignAdmin, permissions: assignPerms });
      toast.success('Bot assigned');
      setAssignOpen(false);
      fetchBots();
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const unassignAdmin = async (botId, adminId) => {
    try {
      await API.delete(`/bots/${botId}/assign/${adminId}`);
      toast.success('Admin unassigned');
      fetchBots();
      fetchAdmins();
    } catch { toast.error('Failed'); }
  };

  const togglePerm = (perm) => {
    setAssignPerms(prev => prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]);
  };

  const moveBot = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= bots.length) return;
    const order = bots.map(b => b.id);
    [order[index], order[target]] = [order[target], order[index]];
    const reordered = order.map(id => bots.find(b => b.id === id));
    setBots(reordered);
    setGlobalBots(reordered);
    try {
      await API.put('/bots/reorder', { order });
      toast.success('Bot order updated');
    } catch { toast.error('Failed to reorder'); fetchBots(); }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="bot-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Bot Management</h1>
          <p className="text-sm text-muted-foreground mt-1">{bots.length} bot{bots.length !== 1 ? 's' : ''} registered</p>
        </div>
        <Button data-testid="add-bot-btn" onClick={() => setAddOpen(true)} className="gap-2">
          <Plus size={18} /> Add Bot
        </Button>
      </div>

      <div className="space-y-2.5">
        {bots.map((bot, idx) => (
          <div
            key={bot.id}
            className="border border-border rounded-lg p-3 bg-card hover:border-border/80 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3"
            data-testid={`bot-card-${bot.id}`}
          >
            {/* Left: Position & Bot Info */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex items-center gap-1 bg-muted/40 rounded px-1.5 py-0.5 border border-border/50 shrink-0">
                <span className="text-xs font-bold text-muted-foreground font-mono" data-testid={`bot-position-${bot.id}`}>#{idx + 1}</span>
                <div className="flex flex-col">
                  <button
                    data-testid={`move-bot-up-${bot.id}`}
                    onClick={() => moveBot(idx, -1)}
                    disabled={idx === 0}
                    className={`p-0.5 rounded hover:bg-accent ${idx === 0 ? 'opacity-20 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground'}`}
                    title="Move up"
                  >
                    <CaretUp size={12} weight="bold" />
                  </button>
                  <button
                    data-testid={`move-bot-down-${bot.id}`}
                    onClick={() => moveBot(idx, 1)}
                    disabled={idx === bots.length - 1}
                    className={`p-0.5 rounded hover:bg-accent ${idx === bots.length - 1 ? 'opacity-20 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground'}`}
                    title="Move down"
                  >
                    <CaretDown size={12} weight="bold" />
                  </button>
                </div>
              </div>

              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                <Robot size={18} weight="duotone" />
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-sm sm:text-base truncate leading-snug">{bot.bot_name}</h3>
                  {bot.is_blocked ? (
                    <Badge variant="destructive" className="text-[10px] px-1.5 py-0 bg-red-600 text-white flex items-center gap-0.5" data-testid={`bot-blocked-badge-${bot.id}`}>
                      <Prohibit size={11} weight="bold" /> Blocked
                    </Badge>
                  ) : (
                    <Badge variant={bot.is_active ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0">
                      {bot.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">@{bot.bot_username}</p>
              </div>
            </div>

            {/* Middle: Compact Stats */}
            <div className="flex items-center gap-2.5 text-xs text-muted-foreground shrink-0 pl-11 sm:pl-0">
              <span className="inline-flex items-center gap-1 font-mono bg-muted/40 px-2 py-1 rounded border border-border/40">
                👥 {bot.user_count || 0} users
              </span>
              <span
                onClick={() => openAssign(bot)}
                title={
                  bot.assigned_admins?.length > 0
                    ? `Assigned Admins: ${bot.assigned_admins
                        .map(aId => {
                          const a = admins.find(x => x.id === aId || x._id === aId);
                          return a ? `${a.name} (${a.email})` : `Admin (${aId.slice(0, 8)}...)`;
                        })
                        .join(', ')} (Click to manage)`
                    : 'Click to assign admins'
                }
                className={`inline-flex items-center gap-1 font-mono px-2 py-1 rounded border cursor-pointer transition-colors ${
                  bot.assigned_admins?.length > 0
                    ? 'bg-primary/10 text-primary border-primary/30 font-semibold hover:bg-primary/20'
                    : 'bg-muted/40 text-muted-foreground border-border/40 hover:bg-muted'
                }`}
              >
                🛡️ {bot.assigned_admins?.length || 0} admins
              </span>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-1.5 shrink-0 pl-11 sm:pl-0 flex-wrap">
              <Button
                data-testid={`ping-bot-${bot.id}`}
                variant="outline"
                size="sm"
                onClick={() => pingBot(bot.id)}
                disabled={pinging[bot.id]}
                className="h-8 px-2.5 text-xs gap-1"
                title="Ping Bot"
              >
                {pinging[bot.id] ? <CircleNotch size={13} className="animate-spin" /> : <ArrowsClockwise size={13} />}
                Ping
              </Button>
              <Button
                data-testid={`assign-bot-${bot.id}`}
                variant="outline"
                size="sm"
                onClick={() => openAssign(bot)}
                className="h-8 px-2.5 text-xs gap-1"
                title="Assign Admins"
              >
                <UserPlus size={13} />
                Assign
              </Button>
              {user?.role === 'super_admin' && (
                bot.is_blocked ? (
                  <Button
                    data-testid={`unblock-bot-${bot.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleBlockBot(bot)}
                    disabled={blockingBot}
                    className="h-8 px-2.5 text-xs gap-1 border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10"
                    title="Unblock Bot"
                  >
                    <ShieldCheck size={13} />
                    Unblock
                  </Button>
                ) : (
                  <Button
                    data-testid={`block-bot-${bot.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleBlockBot(bot)}
                    disabled={blockingBot}
                    className="h-8 px-2.5 text-xs gap-1 border-red-500/40 text-red-600 hover:bg-red-500/10"
                    title="Block Bot"
                  >
                    <Prohibit size={13} />
                    Block
                  </Button>
                )
              )}
              <Button
                data-testid={`delete-bot-${bot.id}`}
                variant="outline"
                size="sm"
                onClick={() => deleteBot(bot.id)}
                className="h-8 px-2 text-xs gap-1 text-destructive hover:text-destructive"
                title="Delete Bot"
              >
                <Trash size={13} />
              </Button>
              <Button
                data-testid={`manage-bot-${bot.id}`}
                variant="default"
                size="sm"
                onClick={() => { setGlobalSelectedBot(bot.id); navigate('/app/dashboard'); }}
                className="h-8 px-3 text-xs gap-1"
              >
                <ArrowSquareOut size={13} />
                Manage
              </Button>
            </div>
          </div>
        ))}
        {bots.length === 0 && (
          <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-md">
            No bots added yet. Click "Add Bot" to get started.
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Bot from BotFather</DialogTitle></DialogHeader>
          <form onSubmit={addBot} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Bot API Token</Label>
              <Input data-testid="bot-token-input" value={token} onChange={e => setToken(e.target.value)} placeholder="Paste your bot token from BotFather" required />
              <p className="text-xs text-muted-foreground">Bot details will be fetched automatically</p>
            </div>
            <DialogFooter>
              <Button data-testid="bot-add-submit" type="submit" disabled={adding}>
                {adding ? 'Verifying & Adding...' : 'Add Bot'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign Bot: {assignBot?.bot_name}</DialogTitle>
          </DialogHeader>

          {/* Currently Assigned Admins list with unassign action */}
          {assignBot?.assigned_admins?.length > 0 && (
            <div className="space-y-2 border-b border-border pb-4">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Currently Assigned Admins</Label>
              <div className="space-y-1.5">
                {assignBot.assigned_admins.map(aId => {
                  const a = admins.find(x => x.id === aId || x._id === aId);
                  return (
                    <div key={aId} className="flex items-center justify-between p-2 rounded-md bg-muted/40 border border-border/60">
                      <div>
                        <span className="text-sm font-medium">{a ? a.name : 'Assigned Admin'}</span>
                        <span className="text-xs text-muted-foreground ml-2">({a ? a.email : aId})</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          await unassignAdmin(assignBot.id, aId);
                          setAssignBot(prev => ({
                            ...prev,
                            assigned_admins: prev.assigned_admins.filter(id => id !== aId)
                          }));
                        }}
                        className="h-7 px-2 text-destructive hover:text-destructive text-xs gap-1"
                      >
                        <Trash size={12} /> Remove
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-4 pt-1">
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Assign New Admin</Label>
            <div className="space-y-1.5">
              <Label>Select Admin</Label>
              <select data-testid="assign-admin-select" value={assignAdmin} onChange={e => handleAdminSelect(e.target.value)} className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background">
                <option value="">Choose admin...</option>
                {admins.filter(a => a.is_active).map(a => <option key={a.id} value={a.id}>{a.name} ({a.email})</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Permissions</Label>
              <p className="text-xs text-muted-foreground">Select which features this admin can access</p>
              {ALL_PERMISSIONS.map(p => (
                <label key={p.key} className="flex items-center gap-2 cursor-pointer py-1">
                  <Checkbox checked={assignPerms.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} data-testid={`perm-${p.key}`} />
                  <span className="text-sm">{p.label}</span>
                  {assignPerms.includes(p.key) && <CheckCircle size={14} className="text-green-500 ml-auto" />}
                </label>
              ))}
            </div>
            <DialogFooter>
              <Button data-testid="assign-submit-btn" onClick={submitAssign}>Assign</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog to Block Bot */}
      <Dialog open={!!confirmBlockBot} onOpenChange={(open) => !open && setConfirmBlockBot(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Prohibit size={20} />
              Block Bot @{confirmBlockBot?.bot_username || confirmBlockBot?.bot_name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 text-sm text-muted-foreground space-y-2">
            <p>
              Are you sure you want to block this bot?
            </p>
            <ul className="list-disc pl-5 space-y-1 text-xs">
              <li>Automatic webhook responses and incoming messages will be suspended.</li>
              <li>Live chat replies and broadcasts using this bot will be prevented.</li>
              <li>Only a Super Admin can unblock this bot later.</li>
            </ul>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmBlockBot(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmBlockBot} disabled={blockingBot} data-testid="confirm-block-bot-btn">
              {blockingBot ? 'Blocking...' : 'Confirm Block'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
